/**
 * Eine Kasse von Hand erfassen.
 *
 * Nicht jedes Gerät steht in einem der drei Exporte. Im Wintergarten laufen
 * Terminals einer anderen Firma: selten benutzt, eigenes Format, werden
 * womöglich ersetzt. Dafür einen vierten Adapter zu bauen lohnt nicht — ihre
 * Zahlen werden eingetragen, wenn sie gelaufen sind.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type WorkstationDto } from "../lib/api";
import { ErrorBox, Info } from "./ui";

export function ManualCashEntry({
  businessDate,
  workstations,
  onDone,
  onCancel,
}: {
  businessDate: string;
  workstations: WorkstationDto[];
  onDone: () => void;
  onCancel: () => void;
}) {
  // Die Geräte ohne automatischen Import zuerst: sie sind der Grund für diese Maske.
  const sortiert = [...workstations.filter((w) => w.active)].sort(
    (a, b) =>
      Number(b.manualOnly ?? false) - Number(a.manualOnly ?? false) ||
      a.name.localeCompare(b.name),
  );
  const [workstationId, setWorkstationId] = useState(sortiert[0]?.id ?? "");
  const [umsatz, setUmsatz] = useState("");
  const [kassenschnitt, setKassenschnitt] = useState("");
  const [grund, setGrund] = useState("");
  const queryClient = useQueryClient();

  const speichern = useMutation({
    mutationFn: () =>
      api.post(`/business-days/${businessDate}/cash-sessions`, {
        workstationId,
        ...(umsatz.trim() ? { zBon: normalize(umsatz) } : {}),
        ...(kassenschnitt.trim() ? { cardTotal: normalize(kassenschnitt) } : {}),
        reason: grund.trim() === "" ? undefined : grund.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onDone();
    },
  });

  const gewaehlt = sortiert.find((w) => w.id === workstationId);
  const nichts = umsatz.trim() === "" && kassenschnitt.trim() === "";

  return (
    <div className="card mb-6 border-accent/40 p-5">
      <h3 className="mb-1 text-lg">Kasse von Hand erfassen</h3>
      <p className="mb-4 max-w-2xl text-sm text-muted">
        Für Geräte, die in keinem der drei Exporte stehen. Die Zeile gilt sofort als
        bestätigt: Sie kommt aus keinem Abgleich, also gibt es nichts zu klären. Auch ein
        späteres Abgleichen lässt sie stehen.
      </p>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <label className="block sm:col-span-3">
          <span className="label mb-1 block">Kasse</span>
          <select
            className="field"
            value={workstationId}
            onChange={(e) => setWorkstationId(e.target.value)}
          >
            {sortiert.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
                {w.manualOnly ? " · nur manuell" : ""}
                {w.poolCode ? ` · ${w.poolCode}` : ""}
              </option>
            ))}
          </select>
          {gewaehlt && !gewaehlt.manualOnly ? (
            <span className="mt-1 block text-xs text-warn">
              Diese Kasse kommt normalerweise aus dem Import. Eine Handeingabe steht dann
              zusätzlich daneben.
            </span>
          ) : null}
        </label>

        <label className="block">
          <span className="label mb-1 block">Umsatz</span>
          <input
            className="field num"
            value={umsatz}
            onChange={(e) => setUmsatz(e.target.value)}
            placeholder="480,00"
            inputMode="decimal"
          />
        </label>

        <label className="block">
          <span className="label mb-1 block">davon am Terminal</span>
          <input
            className="field num"
            value={kassenschnitt}
            onChange={(e) => setKassenschnitt(e.target.value)}
            placeholder="512,50"
            inputMode="decimal"
          />
        </label>

        <div className="flex items-end pb-2 text-xs text-muted">
          Nur ein Feld gefüllt heißt: kein Trinkgeld.
        </div>
      </div>

      <label className="mb-4 block max-w-lg">
        <span className="label mb-1 block">
          Grund (freiwillig)
          <Info text="Hat das Gerät eine eigene Belegnummer, gehört sie hierhin. Dann lässt sich der Betrag später bis zum Ausdruck zurückverfolgen." />
        </span>
        <input
          className="field"
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
          placeholder="z. B. Wintergarten-Gerät 2, Beleg 4711"
        />
      </label>

      {speichern.error ? <ErrorBox error={speichern.error} /> : null}

      <div className="flex gap-2">
        <button
          className="btn-recommend"
          disabled={nichts || workstationId === "" || speichern.isPending}
          onClick={() => speichern.mutate()}
        >
          {speichern.isPending ? "Speichert" : "Erfassen"}
        </button>
        <button className="btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

/** Deutsche Eingabe (`1.234,56`) in das API-Format (`1234.56`). */
function normalize(value: string): string {
  const trimmed = value.trim().replace(/\s/g, "");
  if (trimmed.includes(",")) return trimmed.replace(/\./g, "").replace(",", ".");
  return trimmed;
}
